import orderBy from 'lodash/orderBy';
import uniq from 'lodash/uniq';

import { AutomationChannel } from 'apis/automations';
import { CustomFieldAttribute } from 'apis/customFields';
import { FlowType } from 'fable/flowEditor';

export enum SystemVariableId {
  Body = 'body',
  CallerNumber = 'caller_number',
  CCEmails = 'cc_emails',
  Channel = 'channel',
  CSID = 'csid',
  Date = 'date',
  DateRange = 'date_range',
  Direction = 'direction',
  FromEmail = 'from_email',
  FromName = 'from_name',
  Language = 'language',
  ReceivingNumber = 'receiving_number',
  Subject = 'subject',
  SubjectOrBody = 'subject_or_body',
  ToEmail = 'to_email',
  EnduserExternalId = 'enduser_external_id',
  ContactCustomAttributePrefix = 'contact_custom_attribute:',
  ConversationCustomAttributePrefix = 'conversation_custom_attribute:',
  CustomFormAttributePrefix = 'custom_form_attribute:',
  Set = 'set',
  AuthLevel = 'auth_level',
  TriggeringFormId = 'triggering_form_id',
  AutomationFromEmail = 'fromEmail',
  AutomationFrom = 'from',
  AutomationTo = 'to',
  AutomationFromName = 'fromName',
  AutomationExternalId = 'externalId',
  AutomationConversationId = 'conversationId',
  AutomationFromNumber = 'fromNumber',
  AutomationToNumber = 'toNumber',
  AutomationContactEmail = 'contactEmail',
  AutomationContactPhone = 'contactPhone',
  AutomationContactName = 'contactName',
  AutomationContactFacebookId = 'contactFacebookId',
  AutomationContactInstagramUsername = 'contactInstagramUsername',
  AutomationChannelId = 'channelId',
}

export enum SystemVariableType {
  Input = 'input',
  Scope = 'scope',
  System = 'system',
}

type ScopeVarsT = {
  available: string[];
};

export type PropsT = {
  scopeVars: ScopeVarsT;
  flowType: FlowType;
  automationChannels?: AutomationChannel[];
  noChannelVars?: boolean;
  conversationCustomAttributes?: readonly CustomFieldAttribute[];
};

export type FlowVarItemT = {
  name: string;
  content: string;
};

export type FlowCategoryT = {
  label: string;
  items: FlowVarItemT[];
};

export type FlowCategorySelectItem = {
  name: string;
  id: `${SystemVariableType.Scope | SystemVariableType.System}:${
    | string
    | SystemVariableId}`;
  description: string;
};

export type FlowCategoriesT = FlowCategoryT[];

export type CallOrEmailFlowTypeT =
  | FlowType.FLOW_TYPE_CALL
  | FlowType.FLOW_TYPE_EMAIL;

export const FLOW_TYPE_CALL = 'FLOW_TYPE_CALL';
export const FLOW_TYPE_EMAIL = 'FLOW_TYPE_EMAIL';
const REGEX_VALID_NAME = /(:)/;

export const isValidVariableName = (name: string): boolean => {
  return !REGEX_VALID_NAME.test(name);
};

export const getVariableNameErrorMessage = (name: string): null | string => {
  const match = REGEX_VALID_NAME.exec(name);
  if (match === null) {
    return null;
  }
  return `Contains invalid characters "${match[1]}"`;
};

export function getSystemVariableString(
  id: SystemVariableId,
): `${SystemVariableType.System}:${SystemVariableId}` {
  return `${SystemVariableType.System}:${id}`;
}

export function extractAttributeWithPrefix(
  maybeRawAttribute: unknown,
  prefix: SystemVariableId,
): [`${SystemVariableType.System}:${SystemVariableId}`, string] | [] {
  if (
    typeof maybeRawAttribute !== 'string' ||
    !maybeRawAttribute.includes(prefix)
  ) {
    return [];
  }

  const attrVariable = getSystemVariableString(prefix);
  const maybeId = maybeRawAttribute.replace(attrVariable, '');
  if (!maybeId) {
    return [];
  }

  return [attrVariable, maybeId];
}

export function checkAndExtractCustomAttribute(
  maybeRawAttribute?: unknown,
): [`${SystemVariableType.System}:${SystemVariableId}`, string] | [] {
  if (
    typeof maybeRawAttribute !== 'string' ||
    !maybeRawAttribute.includes(SystemVariableId.ContactCustomAttributePrefix)
  ) {
    return [];
  }

  const attrVariable = getSystemVariableString(
    SystemVariableId.ContactCustomAttributePrefix,
  );
  const maybeId = maybeRawAttribute.replace(attrVariable, '');
  if (!maybeId) {
    return [];
  }

  return [attrVariable, maybeId];
}

type ExtractCustomFormAttributeResults = {
  attrVariable: `${SystemVariableType.System}:${SystemVariableId}`;
  formId: string;
  formFieldId: string;
};

/**
 * Extracts the custom form id and form field id from the given value The value
 * is formatted as `system:custom_form_attribute:<form_id>:<form_field_id>`
 */
export function checkAndExtractCustomFormAttribute(
  maybeRawAttribute?: unknown,
): ExtractCustomFormAttributeResults | undefined {
  if (
    typeof maybeRawAttribute !== 'string' ||
    !maybeRawAttribute.includes(SystemVariableId.CustomFormAttributePrefix)
  ) {
    return undefined;
  }

  const attrVariable = getSystemVariableString(
    SystemVariableId.CustomFormAttributePrefix,
  );

  const maybeIds = maybeRawAttribute.replace(attrVariable, '');

  if (!maybeIds) {
    return undefined;
  }

  const [formId, formFieldId] = maybeIds.split(':');

  return {
    attrVariable,
    formId,
    formFieldId,
  };
}

export const getAvailableFlowVariables = (props: PropsT): FlowCategoriesT => {
  const { scopeVars, flowType } = props;
  const flowCategories = [
    {
      label: 'Flow variables',
      items: orderBy(
        uniq(scopeVars.available).map(
          (scopeVar: string): FlowVarItemT => ({
            name: `scope:${scopeVar}`,
            content: scopeVar,
          }),
          'name',
        ),
      ),
    },
  ];

  switch (flowType) {
    case FlowType.FLOW_TYPE_CALL:
      flowCategories.push({
        label: 'Conversation data',
        items: [
          {
            name: getSystemVariableString(SystemVariableId.CSID),
            content: 'Conversation ID',
          },
          {
            name: getSystemVariableString(SystemVariableId.CallerNumber),
            content: 'Caller number',
          },
          {
            name: getSystemVariableString(SystemVariableId.ReceivingNumber),
            content: 'Receiving number',
          },
          {
            name: getSystemVariableString(SystemVariableId.EnduserExternalId),
            content: 'External ID',
          },
        ],
      });
      break;
    case FlowType.FLOW_TYPE_EMAIL:
      flowCategories.push({
        label: 'Conversation data',
        items: [
          {
            name: getSystemVariableString(SystemVariableId.CSID),
            content: 'Conversation ID',
          },
          {
            name: getSystemVariableString(SystemVariableId.Channel),
            content: 'Channel',
          },
          {
            name: getSystemVariableString(SystemVariableId.Direction),
            content: 'Direction',
          },
          {
            name: getSystemVariableString(SystemVariableId.FromEmail),
            content: 'From email',
          },
          {
            name: getSystemVariableString(SystemVariableId.ToEmail),
            content: 'To email',
          },
          {
            name: getSystemVariableString(SystemVariableId.FromName),
            content: 'From name',
          },
          {
            name: getSystemVariableString(SystemVariableId.SubjectOrBody),
            content: 'Subject or body',
          },
          {
            name: getSystemVariableString(SystemVariableId.Subject),
            content: 'Subject',
          },
          {
            name: getSystemVariableString(SystemVariableId.Body),
            content: 'Body',
          },
          {
            name: getSystemVariableString(SystemVariableId.CCEmails),
            content: 'CC emails',
          },
          {
            name: getSystemVariableString(SystemVariableId.Language),
            content: 'Language',
          },
          {
            name: getSystemVariableString(SystemVariableId.EnduserExternalId),
            content: 'External ID',
          },
        ],
      });
      break;

    case FlowType.FLOW_TYPE_MESSENGER:
      flowCategories.push({
        label: 'Conversation data',
        items: [
          {
            name: getSystemVariableString(SystemVariableId.Language),
            content: 'Language',
          },
          {
            name: getSystemVariableString(SystemVariableId.FromEmail),
            content: 'From email',
          },
          {
            name: getSystemVariableString(SystemVariableId.FromName),
            content: 'From name',
          },
          {
            name: getSystemVariableString(SystemVariableId.CSID),
            content: 'Conversation ID',
          },
          {
            name: getSystemVariableString(SystemVariableId.EnduserExternalId),
            content: 'External ID',
          },
        ],
      });
      break;

    case FlowType.FLOW_TYPE_AUTOMATION:
      const { noChannelVars, conversationCustomAttributes } = props;

      flowCategories.push({
        label: 'Contact data',
        items: [
          {
            name: getSystemVariableString(
              SystemVariableId.AutomationContactName,
            ),
            content: 'Contact name',
          },
          {
            name: getSystemVariableString(
              SystemVariableId.AutomationContactEmail,
            ),
            content: 'Contact email',
          },
          {
            name: getSystemVariableString(
              SystemVariableId.AutomationContactPhone,
            ),
            content: 'Contact phone',
          },
          {
            name: getSystemVariableString(
              SystemVariableId.AutomationContactFacebookId,
            ),
            content: 'Contact Facebook ID',
          },
          {
            name: getSystemVariableString(
              SystemVariableId.AutomationContactInstagramUsername,
            ),
            content: 'Contact Instagram username',
          },
        ],
      });

      flowCategories.push({
        label: 'Conversation data',
        items: [
          ...(!noChannelVars
            ? [
                {
                  name: getSystemVariableString(
                    SystemVariableId.AutomationFrom,
                  ),
                  content: 'From',
                },
                {
                  name: getSystemVariableString(SystemVariableId.AutomationTo),
                  content: 'To',
                },
              ]
            : []),
          {
            name: getSystemVariableString(
              SystemVariableId.AutomationExternalId,
            ),
            content: 'External ID',
          },
          {
            name: getSystemVariableString(
              SystemVariableId.AutomationConversationId,
            ),
            content: 'Conversation ID',
          },
        ],
      });

      const activeConversationCustomAttributes =
        conversationCustomAttributes?.filter(
          (attr) => !attr.is_archived && !attr.is_deactivated,
        ) ?? [];

      if (activeConversationCustomAttributes.length) {
        flowCategories.push({
          label: 'Custom conv. attributes',
          items: activeConversationCustomAttributes.map((attr) => ({
            name: `${SystemVariableType.System}:${SystemVariableId.ConversationCustomAttributePrefix}${attr.id}`,
            content: attr.label,
          })),
        });
      }
  }

  return flowCategories;
};
